/**
 * Bake EXIF orientation into pixels for the unified image editor.
 *
 * react-easy-crop applies CSS `transform` (and `will-change: transform`).
 * Browsers then ignore EXIF `Orientation` on the <img>, so a phone photo
 * appears sideways while the rotation slider stays at 0°. Canvas export
 * has the same problem. The cropper and getCroppedImg must see an upright
 * bitmap with no EXIF.
 */

const JPEG_SOI = 0xffd8;
const JPEG_SOS = 0xffda;
const JPEG_EOI = 0xffd9;
const JPEG_APP1 = 0xffe1;
const TIFF_ORIENTATION_TAG = 0x0112;

function isSofMarker(marker) {
  return (
    (marker >= 0xffc0 && marker <= 0xffc3) ||
    (marker >= 0xffc5 && marker <= 0xffc7) ||
    (marker >= 0xffc9 && marker <= 0xffcb) ||
    (marker >= 0xffcd && marker <= 0xffcf)
  );
}

function readExifOrientationFromApp1(view, app1Offset) {
  const app1Length = view.getUint16(app1Offset + 2, false);
  const payloadStart = app1Offset + 4;
  const payloadEnd = app1Offset + 2 + app1Length;
  if (payloadEnd > view.byteLength || payloadStart + 6 > payloadEnd) {
    return null;
  }

  const exifHeader = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];
  for (let i = 0; i < exifHeader.length; i += 1) {
    if (view.getUint8(payloadStart + i) !== exifHeader[i]) {
      return null;
    }
  }

  const tiffStart = payloadStart + 6;
  if (tiffStart + 8 > payloadEnd) {
    return null;
  }

  const endianMark = view.getUint16(tiffStart, false);
  const littleEndian = endianMark === 0x4949;
  if (!littleEndian && endianMark !== 0x4d4d) {
    return null;
  }

  const fortyTwo = view.getUint16(tiffStart + 2, littleEndian);
  if (fortyTwo !== 42) {
    return null;
  }

  const ifdOffset = view.getUint32(tiffStart + 4, littleEndian);
  const ifdStart = tiffStart + ifdOffset;
  if (ifdStart + 2 > payloadEnd) {
    return null;
  }

  const entryCount = view.getUint16(ifdStart, littleEndian);
  for (let i = 0; i < entryCount; i += 1) {
    const entry = ifdStart + 2 + i * 12;
    if (entry + 12 > payloadEnd) {
      break;
    }
    const tag = view.getUint16(entry, littleEndian);
    if (tag !== TIFF_ORIENTATION_TAG) {
      continue;
    }
    const value = view.getUint16(entry + 8, littleEndian);
    if (value >= 1 && value <= 8) {
      return value;
    }
  }
  return null;
}

/**
 * @param {ArrayBuffer|ArrayBufferView} buffer
 * @returns {{ orientation: number, width: number, height: number }}
 */
export function readJpegExifMetadata(buffer) {
  const view = buffer instanceof DataView
    ? buffer
    : new DataView(buffer instanceof ArrayBuffer ? buffer : buffer.buffer);
  const empty = { orientation: 1, width: 0, height: 0 };
  if (view.byteLength < 4 || view.getUint16(0, false) !== JPEG_SOI) {
    return empty;
  }

  let offset = 2;
  let orientation = 1;
  let width = 0;
  let height = 0;

  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1;
      continue;
    }

    const marker = view.getUint16(offset, false);
    if (marker === JPEG_SOI) {
      offset += 2;
      continue;
    }
    if (marker === JPEG_EOI || marker === JPEG_SOS) {
      break;
    }
    if (marker === 0xff00 || marker === 0xff01 || (marker >= 0xffd0 && marker <= 0xffd7)) {
      offset += 2;
      continue;
    }

    const size = view.getUint16(offset + 2, false);
    if (size < 2) {
      break;
    }

    if (marker === JPEG_APP1 && orientation === 1) {
      const parsed = readExifOrientationFromApp1(view, offset);
      if (parsed) {
        orientation = parsed;
      }
    }

    if (isSofMarker(marker) && offset + 9 <= view.byteLength) {
      height = view.getUint16(offset + 5, false);
      width = view.getUint16(offset + 7, false);
    }

    offset += 2 + size;
  }

  return { orientation, width, height };
}

export function orientationSwapsAxes(orientation) {
  return orientation >= 5 && orientation <= 8;
}

/**
 * True when the decoded bitmap is still in the raw sensor grid and must be
 * transposed to match EXIF Orientation (typical phone portrait JPEG).
 */
export function needsOrientationDraw(bitmapSize, metadata) {
  const orientation = metadata?.orientation ?? 1;
  if (orientation === 1 || !bitmapSize?.width || !bitmapSize?.height) {
    return false;
  }
  if (!metadata.width || !metadata.height) {
    return orientation !== 1;
  }
  const swapped = orientationSwapsAxes(orientation);
  const matchesRaw = bitmapSize.width === metadata.width && bitmapSize.height === metadata.height;
  const matchesOriented =
    swapped && bitmapSize.width === metadata.height && bitmapSize.height === metadata.width;
  if (matchesOriented) {
    return false;
  }
  return matchesRaw || orientation !== 1;
}

export function canvasSizeForOrientation(width, height, orientation) {
  if (orientationSwapsAxes(orientation)) {
    return { width: height, height: width };
  }
  return { width, height };
}

/** Mutates `ctx` so `drawImage(image, 0, 0)` writes upright pixels. */
export function transformContextForOrientation(ctx, width, height, orientation) {
  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      ctx.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      ctx.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx.transform(0, 1, -1, 0, height, 0);
      break;
    case 7:
      ctx.transform(0, -1, -1, 0, height, width);
      break;
    case 8:
      ctx.transform(0, -1, 1, 0, 0, width);
      break;
    default:
      break;
  }
}

function blobToArrayBuffer(blob) {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read image bytes'));
    reader.readAsArrayBuffer(blob);
  });
}

async function toBlob(source) {
  if (source instanceof Blob) {
    return source;
  }
  if (typeof source !== 'string' || !source) {
    throw new Error('Unsupported image source');
  }
  const response = await fetch(source);
  const blob = await response.blob();
  if (!(blob instanceof Blob)) {
    throw new Error('Image source did not resolve to a blob');
  }
  return blob;
}

async function decodeBitmap(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob, { imageOrientation: 'none' });
    } catch {
      try {
        return await createImageBitmap(blob);
      } catch {
        /* fall through to Image() */
      }
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.addEventListener('load', () => resolve(img));
      img.addEventListener('error', (error) => reject(error));
      img.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function canvasToBlob(canvas, mimeType) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Oriented canvas is empty'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      mimeType === 'image/jpeg' ? 0.95 : undefined,
    );
  });
}

function outputMimeType(blob) {
  if (blob.type === 'image/png' || blob.type === 'image/webp') {
    return blob.type;
  }
  return 'image/jpeg';
}

/**
 * @param {Blob|File|string} source raw file, blob, data URL, or blob URL
 * @param {{ revokeInputBlobUrl?: boolean }} [options]
 * @returns {Promise<{ src: string, orientation: number, revoke: () => void }>}
 */
export async function bakeOrientedImageSrc(source, { revokeInputBlobUrl = false } = {}) {
  const blob = await toBlob(source);
  if (revokeInputBlobUrl && typeof source === 'string' && source.startsWith('blob:')) {
    URL.revokeObjectURL(source);
    source = blob;
  }

  const metadata = readJpegExifMetadata(await blobToArrayBuffer(blob));

  const bitmap = await decodeBitmap(blob);
  const naturalWidth = bitmap.naturalWidth || bitmap.width;
  const naturalHeight = bitmap.naturalHeight || bitmap.height;
  const mustDraw = needsOrientationDraw(
    { width: naturalWidth, height: naturalHeight },
    metadata,
  );

  if (!mustDraw && metadata.orientation === 1 && typeof source === 'string') {
    return { src: source, orientation: 1, revoke: () => {} };
  }

  if (!mustDraw && metadata.orientation === 1) {
    const url = URL.createObjectURL(blob);
    return { src: url, orientation: 1, revoke: () => URL.revokeObjectURL(url) };
  }

  const orientation = mustDraw ? metadata.orientation : 1;
  const size = mustDraw
    ? canvasSizeForOrientation(naturalWidth, naturalHeight, orientation)
    : { width: naturalWidth, height: naturalHeight };

  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  if (mustDraw) {
    transformContextForOrientation(ctx, naturalWidth, naturalHeight, orientation);
  }
  ctx.drawImage(bitmap, 0, 0);
  if (typeof bitmap.close === 'function') {
    bitmap.close();
  }

  const orientedBlob = await canvasToBlob(canvas, outputMimeType(blob));
  const url = URL.createObjectURL(orientedBlob);
  return {
    src: url,
    orientation: metadata.orientation,
    revoke: () => URL.revokeObjectURL(url),
  };
}
